import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectorRef 
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ICarrier } from 'src/app/models/ICarrier.model';
import { Orders } from 'src/app/models/Orders.model';
import { Status } from 'src/app/models/status';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { DropdownService } from 'src/app/services/shared/dropdown.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

@Component({
  selector: 'app-confirm-shipment',
  templateUrl: './confirm-shipment.component.html',
  styleUrls: ['./confirm-shipment.component.scss'],
  standalone: false,
})


export class ConfirmShipmentComponent implements OnInit {

  @Input() isShipOn = true;
  @Output() modalClosed1 = new EventEmitter<void>();

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrdersService);
  private dropdownServ = inject(DropdownService);
  public modalService = inject(OpenModalService);
  private cdRef = inject(ChangeDetectorRef);

  // === State ===
  public order: Orders.Order | null = null;
  public orderToUpdate: Orders.OrderUpdate | null = null;
  public carriers: ICarrier[] = [];

  public id = '';
  public loading = false;
  public isLabel = false;
  public isLabelGenerated = false;
  public canGenerate = false;

  /**
   * 🚨 FIX v2.11.1 (#2): convertito da field a getter. La versione legacy
   * computava `correctAddress` UNA SOLA VOLTA in `reloadFunction()` sul
   * snapshot iniziale di `this.order`. Quando l'utente modificava i field
   * dell'indirizzo nel form (ngModel su `orderToUpdate.or_shipping_*`),
   * `correctAddress` restava al valore iniziale → il bottone "Verifica &
   * salva" restava disabled anche dopo aver completato l'indirizzo →
   * impossibile confermare la spedizione. Ora è ricalcolato ad ogni CD
   * cycle sui field correnti di `orderToUpdate`.
   */
  get correctAddress(): boolean {
    if (!this.orderToUpdate) return false;
    const trimNonEmpty = (s: string | null | undefined): boolean =>
      !!(s && s.trim().length);
    return (
      trimNonEmpty(this.orderToUpdate.or_shipping_address) &&
      trimNonEmpty(this.orderToUpdate.or_shipping_customer) &&
      trimNonEmpty(this.orderToUpdate.or_shipping_city) &&
      trimNonEmpty(this.orderToUpdate.or_shipping_prov) &&
      trimNonEmpty(this.orderToUpdate.or_shipping_zip) &&
      trimNonEmpty(this.orderToUpdate.or_shipping_country)
    );
  }


  // === Lifecycle ===

  ngOnInit(): void {
    this.reloadFunction();
  }

  close(): void {
    this.modalService.shipOff();
  }


  /**
   * 🚨 FIX BUG: legacy aveva nested subscribe `getOrder1` → `getCarriers`
   * senza cleanup. Ora forkJoin parallelo + takeUntilDestroyed.
   */
  reloadFunction(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) return;


    this.loading = true;
    forkJoin({
      order: this.orderService.getOrder1(+this.id).pipe(catchError(() => of(null))),
      carriers: this.dropdownServ.getCarriers().pipe(catchError(() => of([]))),
    })
      .subscribe(({ order, carriers }) => {
        if (!order) {
          this.loading = false;
          this.cdRef.detectChanges();  
          return;
        }
        
        this.order = order;
        this.carriers = carriers ?? [];
        this.isLabelGenerated = this.isLabel;
        this.loading = false;
        
        this.orderToUpdate = new Orders.OrderUpdate(this.order);
        if (this.orderToUpdate.or_carrier_id === 0) {
          this.orderToUpdate.or_carrier_id = null;
        }
        if (
          this.order.vs_details?.sp_carrier_id === '0' ||
          this.order.vs_details?.sp_carrier_id == null
        ) {
          this.order.vs_details.sp_carrier_id = null;
        }

        // `correctAddress` è ora getter (v2.11.1) — ricalcolato live sui
        // field bound a ngModel. Non serve più l'assegnazione qui.
        this.canGenerate =
          this.orderToUpdate.or_carrier_id != null &&
          this.order.vs_details?.sp_carrier_id != null;

        this.checkSelectedAll();
        this.cdRef.detectChanges();
      });
  }


  // === Carrier ===

  /**
   * Normalizza `or_carrier_id` (può essere stringa "Other" → other field).
   */
  private normalizeCarrierFields(): void {
    if (!this.orderToUpdate) return;
    if (typeof this.orderToUpdate.or_carrier_id === 'string') {
      this.orderToUpdate.or_carrier_other = this.orderToUpdate.or_carrier_id;
      this.orderToUpdate.or_carrier_id = null;
    }
    this.orderToUpdate.or_carrier_other =
      this.orderToUpdate.or_carrier_id != null
        ? null
        : this.orderToUpdate.or_carrier_other;
  }


  /**
   * 🚨 FIX DRY: legacy aveva `check()` e `updateCor()` quasi-identici
   * (~50 LOC copia-incolla). Estratto helper `persistShippingUpdate()`.
   */
  private persistShippingUpdate(swalTitle: string, swalText: string): void {
    if (!this.order || !this.orderToUpdate) return;
    this.normalizeCarrierFields();


    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.success) {
          // Sync in-memory order from orderToUpdate
          this.order!.or_shipping_address = this.orderToUpdate!.or_shipping_address;
          this.order!.or_shipping_city = this.orderToUpdate!.or_shipping_city;
          this.order!.or_shipping_prov = this.orderToUpdate!.or_shipping_prov;
          this.order!.or_shipping_country = this.orderToUpdate!.or_shipping_country;
          this.order!.or_shipping_customer = this.orderToUpdate!.or_shipping_customer;
          this.order!.or_shipping_zip = this.orderToUpdate!.or_shipping_zip;
          this.order!.or_note = this.orderToUpdate!.or_note;
          this.order!.vs_details.carrier_id = this.orderToUpdate!.or_carrier_id;
          this.order!.vs_details.carrier_other = this.orderToUpdate!.or_carrier_other;
          // `correctAddress` ora è getter (v2.11.1) — niente assegnazione.

          // 🚨 FIX v2.11.2 (Q3): optimistic update dello status BEFORE
          // saveStep+close, così quando il modale si chiude e il parent
          // J.3 ricarica, lo step #4 del wizard è già verde senza
          // bisogno di un secondo click. In v2.11.1 ho aggiunto
          // `applyStatusOptimistic` in J.3 main shell per start/block/
          // unblock/suspend ma NON l'ho applicato qui dentro
          // confirm-shipment.persistShippingUpdate → il bigReload del
          // parent arrivava ancora con il cod vecchio dal BE per via
          // del delay di propagazione setStatus.
          if (this.order?.status?.[0]) {
            this.order.status[0].os_status_order_cod = Status.StatusesUser.step4;
          }
          this.cdRef.detectChanges();
        }
        this.saveStep('products');
        this.modalService.shipOff();
        this.close();
        Swal.fire({
          icon: 'success',
          title: swalTitle,
          text: swalText,
          timer: 1500,
          timerProgressBar: true,
        });
        this.modalClosed1.emit();
      });
  }


  check(): void {
    this.persistShippingUpdate('Verificato!', 'Tutte le informazioni sono corrette.');
  }


  updateCor(): void {
    this.persistShippingUpdate('Aggiornato!', 'Corriere modificato con successo.');
  }


  // === Save step ===

  saveStep(pageName: string): void {
    if (!this.order) return;
    let statusNo = 0;
    if (pageName === 'products') statusNo = Status.StatusesUser.step4;


    this.orderService
      .setStatus(this.order.order_id, statusNo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_res: IWsResult) => {});
  }


  // === Step check ===

  checkSelectedAll(stepBack = false): void {
    if (!this.order) return;
    const stepFrw = 4;
    const stepBck = 3;
    if (!stepBack && (this.isLabel || !this.canGenerate)) {
      this.orderService.pages.next(stepFrw);
    } 
    else {
      this.order.stepCount = stepBck;
      this.orderService.pages.next(stepBck);
      if (this.order.buttons?.[stepBck]) {
        this.order.buttons[stepBck].completed = false;
      }
    }
  }


  // === Label generation ===

  generateLabel(): void {
    if (!this.order) return;
    this.orderService
      .geShipmentLabel(this.order.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const link = result?.data?.[0]?.sm_label_link;
        if (link) {
          this.isLabel = true;
          this.checkSelectedAll();
          window.open(link);
          this.isLabelGenerated = true;
          
          this.cdRef.detectChanges();
        }
      });
  }
  
}